import { CheckCircle2 } from 'lucide-react';
import pluralize from 'pluralize';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { JobLimitGate } from '@/modules/billing/components/jobLimitGate/JobLimitGate';
import { UpgradeButton } from '@/modules/billing/components/UpgradeButton';
import { UsageExceededBanner } from '@/modules/billing/components/usageExceededBanner/UsageExceededBanner';
import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import {
  lacksFeature,
  type WorkspacePlanState,
} from '@/modules/billing/workspacePlan';
import { useCompanyStatsQuery } from '@/modules/companies/queries/useCompanyStatsQuery';
import { HomeChat } from '@/modules/hiring-manager/chat/homeChat/HomeChat';
import { useNewChat } from '@/modules/hiring-manager/chat/useNewChat';
import { HMActiveSearches } from '@/modules/hiring-manager/components/HMActiveSearches';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { HMPendingReviews } from '@/modules/hiring-manager/components/HMPendingReviews';
import { OnboardingChecklist } from '@/modules/hiring-manager/components/OnboardingChecklist';
import {
  PortalList,
  PortalListLimit,
  PortalListRow,
} from '@/modules/hiring-manager/components/PortalList';
import { useCandidateSubmissionsAwaitingReviewQuery } from '@/modules/hiring-manager/queries/useCandidateSubmissionsAwaitingReviewQuery';
import { toSearchSuffix } from '@/modules/hiring-manager/utils/searchSuffix';
import { getHMTenantSelection } from '@/modules/hiring-manager/utils/tenant';
import { thisWeek } from '@/modules/hiring-manager/utils/week';
import { isJobVisibleToHiringManagers } from '@/modules/jobs/jobStates';
import { useJobsQuery } from '@/modules/jobs/queries/useJobsQuery';
import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

const greetingFor = (hour: number) => {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

// Plan limits come from Stigg, never from copy; without one the line leaves the number out.
const welcomeLine = (workspacePlan: WorkspacePlanState) => {
  if (workspacePlan.plan === 'managed') {
    return 'Create a job for your Finlay recruiter to manage. Candidates will appear here when they are ready for your review.';
  }
  const limit = workspacePlan.sourcingLimit;
  const upTo = limit ? ` up to ${pluralize('candidate', limit, true)}` : '';
  if (lacksFeature(workspacePlan, 'outreach')) {
    return `Create a job and Finlay sources${upTo || ' candidates'} for you.`;
  }
  if (workspacePlan.plan === 'free' && upTo) {
    return `Create a job and Finlay sources and contacts${upTo} for you.`;
  }
  return 'Create a job and Finlay starts sourcing, reaching out, and pre-screening for you.';
};

// The first message of a chat opened from a draft; the chat also carries the draft itself.
const continueDraftMessage = (title: string) =>
  title ? `Let's finish my draft "${title}".` : "Let's finish my draft.";

/**
 * Portal home: the greeting carries the day's summary, then the chat with Finlay, then the decision queue and the
 * jobs. A chat started here, from the box or a draft, opens on its own page at once.
 */
export function HMJobs() {
  const [searchParams] = useSearchParams();
  const openNewChat = useNewChat();
  const workspacePlan = useWorkspacePlan();
  const { plan, isLoading, error } = workspacePlan;
  const showSelfServiceControls = !isLoading && !error && plan !== 'managed';

  const currentUserQuery = useCurrentUserQuery();
  const tenantSelection = getHMTenantSelection(
    currentUserQuery.data,
    searchParams,
  );

  // The API cannot filter on several states at once, so all states are fetched
  // and narrowed with the same predicate the rest of the portal uses. A
  // Published-only query would drop paused (OnHold) searches.
  const jobsQuery = useJobsQuery({
    ...(tenantSelection.selectedTenantId
      ? { companyId: tenantSelection.selectedTenantId }
      : {}),
  });
  const candidateSubmissionsQuery = useCandidateSubmissionsAwaitingReviewQuery(
    tenantSelection.selectedTenantId,
  );
  // Every number the stats carry — interviews, LinkedIn outreach, replies —
  // is something only automation produces, so on a plan without it they are
  // all zero and say nothing. Skip the request and the lines it feeds.
  const showsAutomationStats = !lacksFeature(workspacePlan, 'automation');
  const statsQuery = useCompanyStatsQuery(
    showsAutomationStats ? tenantSelection.selectedTenantId : undefined,
    thisWeek(),
  );

  const jobs = (jobsQuery.data ?? []).filter((job) =>
    isJobVisibleToHiringManagers(job.state),
  );
  // Roles the CURRENT USER created that aren't live for the team yet (drafts +
  // unpublished-but-searching). Ownership-filtered on purpose: without it,
  // recruiter WIP jobs would leak to every hiring manager at the company.
  const myPendingRoles = (jobsQuery.data ?? []).filter(
    (job) =>
      !isJobVisibleToHiringManagers(job.state) &&
      // terminal states are hidden, not "pending"
      job.state !== 'Hired' &&
      job.state !== 'Closed' &&
      job.ownerId != null &&
      job.ownerId === currentUserQuery.data?.id,
  );
  // The newest active job, which the chat's examples ask about.
  const recentJob = jobs.reduce<JobApi | undefined>(
    (newest, job) =>
      !newest || Date.parse(job.createdAt) > Date.parse(newest.createdAt)
        ? job
        : newest,
    undefined,
  );
  const candidateSubmissions = candidateSubmissionsQuery.data ?? [];
  const waitingCount = candidateSubmissions.length;
  const hasNoJobs = jobsQuery.isSuccess && (jobsQuery.data?.length ?? 0) === 0;
  const searchSuffix = toSearchSuffix(searchParams);
  const stats = showsAutomationStats ? statsQuery.data : undefined;
  // On Free the summary line only has something to say when a decision is
  // actually waiting; the rest of the sentence is automation's numbers.
  const showsGreetingSummary =
    !hasNoJobs &&
    candidateSubmissionsQuery.isSuccess &&
    (showsAutomationStats || waitingCount > 0);
  // "0 reached, 0 replied" reads as a broken feature rather than a quiet week.
  // On Managed a Finlay recruiter runs the outreach, so it is not theirs to track.
  const showsOutreachCounts =
    !hasNoJobs &&
    stats !== undefined &&
    showSelfServiceControls &&
    (stats.peopleReached > 0 || stats.peopleReplied > 0);
  // Conversations needing review are a standing backlog, not this week's
  // activity, so they stay actionable in a week with no outreach at all.
  const showsConversationsAction =
    !hasNoJobs &&
    stats !== undefined &&
    showSelfServiceControls &&
    stats.conversationsNeedingReview > 0;
  const firstName = currentUserQuery.data?.firstName;

  return (
    <div className="flex flex-col gap-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.02em]">
            {hasNoJobs ? 'Welcome' : greetingFor(new Date().getHours())}
            {firstName ? `, ${firstName}.` : '.'}
          </h1>
          {hasNoJobs && (
            <p className="text-muted-foreground mt-1.5">
              {welcomeLine(workspacePlan)}
            </p>
          )}
          {/* Holds the summary's line while it loads, so the chat box below doesn't jump as the user reaches for it. */}
          {!hasNoJobs && candidateSubmissionsQuery.isPending && (
            <div aria-hidden className="mt-1.5 flex h-5 items-center">
              <Skeleton className="h-3.5 w-72 max-w-full bg-neutral-200" />
            </div>
          )}
          {showsGreetingSummary && (
            <p className="text-muted-foreground mt-1.5 flex items-start gap-2">
              {/* Markers sit in a line box the height of the first text line, so they centre on it however the text wraps. */}
              <span className="flex h-5 shrink-0 items-center">
                {waitingCount > 0 ? (
                  <span className="bg-primary size-1.5 rounded-full" />
                ) : (
                  <CheckCircle2 className="text-semantic-560 size-3.5" />
                )}
              </span>
              <span>
                {waitingCount > 0 ? (
                  <>
                    <Strong>
                      {waitingCount} {pluralize('candidate', waitingCount)}
                    </Strong>{' '}
                    {waitingCount === 1 ? 'is' : 'are'} waiting on you
                  </>
                ) : (
                  'Nothing waiting on you'
                )}
                {stats && (
                  <>
                    {waitingCount > 0 ? ', and you have ' : '. You have '}
                    <Strong>
                      {stats.interviews}{' '}
                      {pluralize('interview', stats.interviews)}
                    </Strong>{' '}
                    this week
                  </>
                )}
                .
              </span>
            </p>
          )}
          {stats && (showsOutreachCounts || showsConversationsAction) && (
            <p className="mt-1 flex items-start gap-2 text-[13px] text-neutral-500">
              <span className="flex h-5 shrink-0 items-center">
                <span className="size-1.5 rounded-full bg-neutral-500" />
              </span>
              <span>
                {showsOutreachCounts && (
                  <>
                    Finlay reached{' '}
                    <span className="tabular-nums">
                      {stats.peopleReached}{' '}
                      {pluralize('person', stats.peopleReached)}
                    </span>{' '}
                    on LinkedIn this week,{' '}
                    <span className="tabular-nums">{stats.peopleReplied}</span>{' '}
                    replied.
                  </>
                )}
                {showsConversationsAction && (
                  <>
                    {showsOutreachCounts && ' '}
                    <Link
                      className="font-medium text-amber-800 hover:underline"
                      to={`/hm/messages${searchSuffix}`}
                    >
                      {stats.conversationsNeedingReview}{' '}
                      {pluralize(
                        'conversation',
                        stats.conversationsNeedingReview,
                      )}{' '}
                      need you
                    </Link>
                    .
                  </>
                )}
              </span>
            </p>
          )}
        </div>
        {!hasNoJobs && (
          // Jobs are created in the chat box below. At the plan's job limit the header offers the plans; in chat,
          // Finlay explains the limit and can still save the job as a draft.
          <JobLimitGate
            reached={() => (
              <UpgradeButton
                className="shrink-0"
                label="Upgrade to add a job"
                size="sm"
                source="hm-job-limit"
                to={`/hm/billing${searchSuffix}`}
                variant="outline"
              />
            )}
          >
            {null}
          </JobLimitGate>
        )}
      </header>

      <HomeChat recentJobTitle={recentJob?.title} />

      {!hasNoJobs && <UsageExceededBanner to={`/hm/billing${searchSuffix}`} />}

      {hasNoJobs && <OnboardingChecklist searchSuffix={searchSuffix} />}

      {showSelfServiceControls && myPendingRoles.length > 0 && (
        <Section
          count={myPendingRoles.length}
          id="hm-pending-roles-heading"
          title="Your drafts"
        >
          <PortalListLimit items={myPendingRoles}>
            {(visible) => (
              <PortalList>
                {visible.map((job) => (
                  <PortalListRow
                    action={
                      job.sourcingState === 'Draft' && (
                        <Button
                          onClick={() =>
                            openNewChat(continueDraftMessage(job.title), job.id)
                          }
                          size="sm"
                          variant="outline"
                        >
                          Continue in chat
                        </Button>
                      )
                    }
                    className="grid-cols-[minmax(0,1fr)_auto_16px]"
                    key={job.id}
                    to={`/hm/jobs/${job.id}/edit${searchSuffix}`}
                  >
                    <span className="truncate font-medium">{job.title}</span>
                    <span className="text-[13px] text-neutral-500">
                      {job.sourcingState === 'Draft'
                        ? 'Draft'
                        : 'Being prepared for your team'}
                    </span>
                  </PortalListRow>
                ))}
              </PortalList>
            )}
          </PortalListLimit>
        </Section>
      )}

      {!hasNoJobs && (
        <Section
          count={
            candidateSubmissionsQuery.data !== undefined
              ? waitingCount
              : undefined
          }
          id="hm-needs-action-heading"
          title="Needs your decision"
        >
          {candidateSubmissionsQuery.isError &&
            candidateSubmissionsQuery.data !== undefined && (
              <HMLoadError
                description="We could not refresh pending reviews. Showing the previously loaded candidates."
                title="Pending reviews may be out of date"
              />
            )}
          {candidateSubmissionsQuery.isError &&
          candidateSubmissionsQuery.data === undefined ? (
            <HMLoadError
              description="We could not load pending reviews. Check your connection and reload the page."
              title="Pending reviews unavailable"
            />
          ) : (
            <HMPendingReviews
              candidateSubmissions={candidateSubmissions}
              isLoading={candidateSubmissionsQuery.isLoading}
              tenantSearch={tenantSelection.tenantSearch}
            />
          )}
        </Section>
      )}

      {!hasNoJobs && (
        <Section
          count={jobsQuery.data !== undefined ? jobs.length : undefined}
          id="hm-active-jobs-heading"
          title="Active jobs"
        >
          {jobsQuery.isError && jobsQuery.data !== undefined && (
            <HMLoadError
              description="We could not refresh active jobs. Showing the previously loaded jobs."
              title="Active jobs may be out of date"
            />
          )}
          {jobsQuery.isError && jobsQuery.data === undefined ? (
            <HMLoadError
              description="We could not load active jobs. Check your connection and reload the page."
              title="Active jobs unavailable"
            />
          ) : (
            <HMActiveSearches
              hasAnyJobs={(jobsQuery.data?.length ?? 0) > 0}
              isLoading={jobsQuery.isLoading}
              jobs={jobs}
              searchSuffix={searchSuffix}
            />
          )}
        </Section>
      )}
    </div>
  );
}

function Strong({ children }: { children: ReactNode }) {
  return (
    <span className="text-foreground font-medium tabular-nums">{children}</span>
  );
}

function Section({
  children,
  count,
  id,
  title,
}: {
  children: ReactNode;
  /** Shown once the section's query has data; unknown counts are not zero. */
  count: number | undefined;
  id: string;
  title: string;
}) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2.5">
      <div className="flex items-baseline gap-2">
        <h2 className="text-[13px] leading-[18px] font-semibold" id={id}>
          {title}
        </h2>
        {count !== undefined && (
          <span className="text-[13px] font-medium text-neutral-500 tabular-nums">
            {count}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}
